import React, { useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";

/**
 * Info icon that shows a tooltip to the right of the icon.
 * Uses a React portal rendered at document.body to escape stacking contexts.
 * All tooltip styling is inline to avoid CSS class display:none conflicts.
 */
const InfoTooltip = ({ info }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const iconRef = useRef(null);

  const show = useCallback(() => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setCoords({ top: rect.top + rect.height / 2, left: rect.right + 10 });
    }
    setVisible(true);
  }, []);

  const hide = useCallback(() => setVisible(false), []);

  const toggle = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (visible) {
      hide();
    } else {
      show();
    }
  }, [visible, show, hide]);

  if (!info || !String(info).trim()) return null;

  return (
    <>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          verticalAlign: "middle",
          lineHeight: 1,
        }}
      >
        <span
          ref={iconRef}
          aria-label="Mer information"
          role="button"
          tabIndex={0}
          className="ids-icon-information ids-icon--s ids-icon--color-preset-1 ids-icon--interactive"
          style={{ marginLeft: "0.35rem", display: "inline-flex", alignItems: "center", justifyContent: "center", verticalAlign: "middle", cursor: "pointer" }}
          onMouseEnter={show}
          onMouseLeave={hide}
          onFocus={show}
          onBlur={hide}
          onClick={toggle}
          onKeyDown={(e) => e.key === "Enter" && toggle(e)}
        />
      </span>
      {visible && createPortal(
        <div
          role="tooltip"
          style={{
            position: "fixed",
            top: coords.top,
            left: coords.left,
            transform: "translateY(-50%)",
            zIndex: 9999,
            maxWidth: "24rem",
            width: "min(24rem, calc(100vw - 2rem))",
            pointerEvents: "none",
            display: "block",
            color: "var(--IDS-TOOLTIP__COLOR)",
            backgroundColor: "var(--IDS-TOOLTIP__BACKGROUND-COLOR)",
            border: "var(--IDS-TOOLTIP__BORDER-THICKNESS, 1px) solid var(--IDS-TOOLTIP__BORDER-COLOR)",
            borderRadius: "var(--IDS-BORDER-RADIUS-MEDIUM, 4px)",
            padding: "var(--IDS-TOOLTIP__PADDING, 0.5rem 0.75rem)",
            fontSize: "1rem",
            lineHeight: "1.5rem",
            fontFamily: "var(--IDS-FONT-FAMILY-BASE, inherit)",
            boxShadow: "var(--IDS-BOX-SHADOW-ELEVATED-1)",
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
            overflow: "hidden",
          }}
        >
          {info}
        </div>,
        document.body
      )}
    </>
  );
};

export default InfoTooltip;
